/**
 * One-time codes: what people paste or what the phone fills in is rarely just the digits — "123456", "123 456",
 * "Your code is 123456." — so every code field reads the code out of whatever text it receives.
 */
export const OTP_LENGTH = 6;

/** The code in `text`: the first run of exactly `length` digits, else all its digits (up to `length`). */
export function codeFromText(text, length = OTP_LENGTH) {
  const s = String(text ?? '');
  const run = s.match(new RegExp(`(?:^|\\D)(\\d{${length}})(?!\\d)`));
  return run ? run[1] : s.replace(/\D/g, '').slice(0, length);
}

/**
 * The boxes after text was pasted into box `i`: a whole code fills every box from the first; a part of one fills
 * from box `i` on. Returns { digits, focus } (the box to focus next), or null when the text has no digits.
 */
export function pasteIntoBoxes(digits, i, text) {
  const n = digits.length;
  const code = codeFromText(text, n);
  if (!code) return null;
  if (code.length === n) return { digits: code.split(''), focus: n - 1 };
  const next = [...digits];
  [...code].forEach((c, k) => { if (i + k < n) next[i + k] = c; });
  return { digits: next, focus: Math.min(i + code.length, n - 1) };
}

/**
 * The boxes after box `i` changed to `value`. Usually one typed digit (typing over a digit leaves both in the box —
 * the new one wins); several digits at once are a code the phone filled in or the keyboard pasted.
 */
export function typeIntoBox(digits, i, value) {
  const typed = String(value ?? '').replace(/\D/g, '');
  const typedOver = typed.length === 2 && digits[i] && typed.includes(digits[i]);
  if (typed.length > 1 && !typedOver) return pasteIntoBoxes(digits, i, value);
  const digit = typedOver ? typed.replace(digits[i], '') : typed;
  const next = [...digits];
  next[i] = digit;
  return { digits: next, focus: digit && i < digits.length - 1 ? i + 1 : i };
}
